import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import {
  TrendingUp,
  TrendingDown,
  Plus,
  Trash2,
  Search,
  ArrowLeft,
  RefreshCw,
  PieChart,
  DollarSign,
  Bitcoin,
  BarChart3,
  X,
  Crown,
  Lock,
  LineChart,
  ChevronRight,
  Edit2,
  Check
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("token");

const LOGO_URL = "/doslions-logo.png";

// Mini Chart Component
function MiniChart({ data, color = "#f59e0b", height = 60 }) {
  if (!data || data.length === 0) return null;
  
  const prices = data.map(d => d.close);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((d.close - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height }} preserveAspectRatio="none">
      <defs>
        <linearGradient id={`gradient-${color.replace('#', '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon 
        points={`0,100 ${points} 100,100`} 
        fill={`url(#gradient-${color.replace('#', '')})`}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

// Stock Search Result Card
function SearchResultCard({ result, onAdd }) {
  const isPositive = parseFloat(result.change) >= 0;
  
  return (
    <div className="flex items-center justify-between p-3 bg-zinc-900/50 border border-zinc-800 hover:border-amber-500/30 transition-colors">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-bold text-white">{result.symbol}</span>
          <span className="text-xs text-zinc-500">{result.exchange}</span>
        </div>
        <p className="text-xs text-zinc-400 truncate max-w-[200px]">{result.name}</p>
      </div>
      <div className="text-right mr-4">
        <p className="font-bold font-data text-white">${result.price}</p>
        <p className={`text-xs font-data ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
          {isPositive ? '+' : ''}{result.change_percent}%
        </p>
      </div>
      <Button
        onClick={() => onAdd(result)}
        size="sm"
        className="bg-amber-500 hover:bg-amber-600 text-black"
      >
        <Plus className="w-4 h-4" />
      </Button>
    </div>
  );
}

// Portfolio Holding Card with Chart
function HoldingCard({ holding, onDelete, onViewChart, onEdit }) {
  const isPositive = holding.profit_loss >= 0;
  
  return (
    <div className="bg-[#111111] border border-zinc-800 hover:border-amber-500/30 transition-colors">
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-bold text-lg text-white">{holding.symbol}</h3>
            <p className="text-xs text-zinc-500">{holding.type.toUpperCase()}</p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onViewChart(holding.symbol)}
              className="text-zinc-500 hover:text-amber-500 h-8 w-8"
            >
              <LineChart className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(holding)}
              className="text-zinc-500 hover:text-amber-500 h-8 w-8"
            >
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(holding.symbol)}
              className="text-zinc-500 hover:text-red-500 h-8 w-8"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-3">
          <div>
            <p className="text-xs text-zinc-500">Quantity</p>
            <p className="font-bold font-data text-white">{holding.quantity}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-500">Current Price</p>
            <p className="font-bold font-data text-white">${holding.current_price}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-500">Avg. Cost</p>
            <p className="font-data text-zinc-400">${holding.purchase_price?.toFixed(2) || '0.00'}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-500">Value</p>
            <p className="font-bold font-data text-amber-500">${holding.current_value_usd?.toLocaleString()}</p>
          </div>
        </div>
        
        <div className={`flex items-center justify-between p-2 ${isPositive ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
          <span className="text-xs text-zinc-400">P/L</span>
          <div className="flex items-center gap-2">
            {isPositive ? (
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500" />
            )}
            <span className={`font-bold font-data ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
              {isPositive ? '+' : ''}${holding.profit_loss?.toFixed(2)} ({holding.profit_loss_percent?.toFixed(2)}%)
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Chart Modal
function ChartModal({ symbol, isOpen, onClose }) {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("1mo");
  
  const periods = [
    { value: "1d", label: "1D" },
    { value: "5d", label: "5D" },
    { value: "1mo", label: "1M" },
    { value: "3mo", label: "3M" },
    { value: "6mo", label: "6M" },
    { value: "1y", label: "1Y" }
  ];
  
  const fetchChart = useCallback(async () => {
    if (!symbol) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/chart/${symbol}?period=${period}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setChartData(response.data);
    } catch (error) {
      toast.error("Failed to load chart");
    } finally {
      setLoading(false);
    }
  }, [symbol, period]);
  
  useEffect(() => {
    if (isOpen && symbol) {
      fetchChart();
    }
  }, [isOpen, symbol, period, fetchChart]);
  
  if (!isOpen) return null;
  
  const isPositive = chartData?.period_change_percent >= 0;
  const chartColor = isPositive ? "#10b981" : "#ef4444";
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-[#111111] border border-amber-500/30 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-6 h-6 text-amber-500" />
            <div>
              <h2 className="font-bold text-xl text-white">{symbol}</h2>
              {chartData && (
                <p className={`text-sm font-data ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
                  ${chartData.current_price} ({isPositive ? '+' : ''}{chartData.period_change_percent?.toFixed(2)}%)
                </p>
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Period Selector */}
        <div className="flex gap-2 p-4 border-b border-zinc-800 overflow-x-auto">
          {periods.map((p) => (
            <Button
              key={p.value}
              variant={period === p.value ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriod(p.value)}
              className={period === p.value 
                ? "bg-amber-500 text-black" 
                : "border-zinc-700 text-zinc-400 hover:border-amber-500"
              }
            >
              {p.label}
            </Button>
          ))}
        </div>
        
        {/* Chart */}
        <div className="p-4">
          {loading ? (
            <div className="h-64 flex items-center justify-center">
              <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
            </div>
          ) : chartData?.chart_data ? (
            <>
              <div className="h-64 mb-4">
                <MiniChart data={chartData.chart_data} color={chartColor} height={256} />
              </div>
              
              {/* Movement Explanation */}
              <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <img src={LOGO_URL} alt="LIONAI" className="w-5 h-5" />
                  <span className="text-xs text-amber-500 font-bold uppercase">LIONAI Analysis</span>
                </div>
                <p className="text-sm text-zinc-300">{chartData.movement_explanation}</p>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="text-center">
                  <p className="text-xs text-zinc-500">Period High</p>
                  <p className="font-bold font-data text-white">
                    ${Math.max(...chartData.chart_data.map(d => d.high)).toFixed(2)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-zinc-500">Period Low</p>
                  <p className="font-bold font-data text-white">
                    ${Math.min(...chartData.chart_data.map(d => d.low)).toFixed(2)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-zinc-500">Data Points</p>
                  <p className="font-bold font-data text-white">{chartData.data_points}</p>
                </div>
              </div>
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-zinc-500">
              No chart data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Add to Portfolio Modal
function AddToPortfolioModal({ isOpen, onClose, searchResult, onAdd }) {
  const [quantity, setQuantity] = useState("1");
  const [purchasePrice, setPurchasePrice] = useState("");
  
  useEffect(() => {
    if (searchResult) {
      setPurchasePrice(searchResult.price || "");
    }
  }, [searchResult]);
  
  if (!isOpen || !searchResult) return null;
  
  const handleAdd = () => {
    const qty = parseFloat(quantity);
    if (isNaN(qty) || qty <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }
    onAdd(searchResult.symbol, qty, parseFloat(purchasePrice) || null);
    onClose();
    setQuantity("1");
  };
  
  const totalValue = (parseFloat(quantity) || 0) * (parseFloat(purchasePrice) || parseFloat(searchResult.price) || 0);
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-[#111111] border border-amber-500/30 rounded-lg w-full max-w-md p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white">
          <X className="w-5 h-5" />
        </button>
        
        <h2 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
          Add to <span className="text-amber-500">Portfolio</span>
        </h2>
        
        <div className="bg-zinc-900/50 p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-white">{searchResult.symbol}</p>
              <p className="text-xs text-zinc-500">{searchResult.name}</p>
            </div>
            <p className="font-bold font-data text-amber-500">${searchResult.price}</p>
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Quantity</label>
            <Input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Enter quantity"
              className="bg-zinc-900 border-zinc-700"
              min="0.001"
              step="any"
            />
          </div>
          
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Purchase Price (optional)</label>
            <Input
              type="number"
              value={purchasePrice}
              onChange={(e) => setPurchasePrice(e.target.value)}
              placeholder={`Current: $${searchResult.price}`}
              className="bg-zinc-900 border-zinc-700"
              step="any"
            />
          </div>
          
          <div className="bg-amber-500/10 border border-amber-500/30 p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-400">Total Value</span>
              <span className="font-bold font-data text-amber-500">${totalValue.toFixed(2)}</span>
            </div>
          </div>
          
          <Button
            onClick={handleAdd}
            className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add to Portfolio
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Portfolio() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [displayInBTC, setDisplayInBTC] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Modals
  const [chartSymbol, setChartSymbol] = useState(null);
  const [addModalResult, setAddModalResult] = useState(null);
  
  const isPro = user?.subscription_tier === "pro" && user?.is_subscribed;
  
  const fetchPortfolio = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portfolio`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setPortfolio(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        // Not subscribed - will show upgrade prompt
      } else {
        toast.error("Failed to load portfolio");
      }
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    if (isPro) {
      fetchPortfolio();
    } else {
      setLoading(false);
    }
  }, [isPro, fetchPortfolio]);
  
  const handleSearch = async (query) => {
    if (!query || query.length < 1) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API}/search/${query}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSearchResults(response.data.results || []);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setSearchLoading(false);
    }
  };
  
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  const addToPortfolio = async (symbol, quantity, purchasePrice) => {
    try {
      await axios.post(`${API}/portfolio`, {
        symbol,
        quantity,
        purchase_price: purchasePrice,
        type: "stock"
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(`Added ${quantity} ${symbol} to portfolio`);
      setSearchQuery("");
      setSearchResults([]);
      fetchPortfolio();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add to portfolio");
    }
  };
  
  const deleteFromPortfolio = async (symbol) => {
    try {
      await axios.delete(`${API}/portfolio/${symbol}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(`Removed ${symbol} from portfolio`);
      fetchPortfolio();
    } catch (error) {
      toast.error("Failed to remove from portfolio");
    }
  };
  
  // Show upgrade prompt for non-Pro users
  if (!isPro) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-amber-500/10 mx-auto flex items-center justify-center mb-6">
            <Lock className="w-10 h-10 text-amber-500" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight uppercase mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            Portfolio <span className="text-amber-500">Locked</span>
          </h1>
          <p className="text-zinc-400 mb-4">
            Create and track your investment portfolio with <span className="text-amber-500 font-bold">Pro Ecopreneur</span>.
          </p>
          <p className="text-sm text-zinc-500 mb-8">
            Add any stock, crypto, or commodity. Track P/L in USD and Bitcoin. View detailed charts with AI analysis.
          </p>
          <div className="space-y-4">
            <Button 
              onClick={() => navigate("/subscription")}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider py-6"
            >
              <Crown className="w-5 h-5 mr-2" />
              Upgrade to Pro Ecopreneur - $49.97
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate("/dashboard")}
              className="w-full border-zinc-700 text-zinc-400"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
              className="text-zinc-400 hover:text-amber-500"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div 
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate("/dashboard")}
              data-testid="logo-home-link"
            >
              <img src={LOGO_URL} alt="ECOLIONS" className="w-8 h-8 object-contain" />
              <h1 className="text-xl font-bold uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed' }}>
                My <span className="text-amber-500">Portfolio</span>
              </h1>
            </div>
          </div>
          
          {/* Currency Toggle */}
          <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 p-1">
            <button
              onClick={() => setDisplayInBTC(false)}
              className={`flex items-center gap-1 px-3 py-1 text-sm ${!displayInBTC ? 'bg-amber-500 text-black' : 'text-zinc-400'}`}
            >
              <DollarSign className="w-4 h-4" /> USD
            </button>
            <button
              onClick={() => setDisplayInBTC(true)}
              className={`flex items-center gap-1 px-3 py-1 text-sm ${displayInBTC ? 'bg-amber-500 text-black' : 'text-zinc-400'}`}
            >
              <Bitcoin className="w-4 h-4" /> BTC
            </button>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Portfolio Summary */}
        {portfolio && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#111111] border border-amber-500/30 p-4">
              <p className="text-xs text-zinc-500 mb-1">Total Value</p>
              <p className="text-2xl font-bold font-data text-amber-500">
                {displayInBTC 
                  ? `₿${portfolio.total_value_btc?.toFixed(6)}` 
                  : `$${portfolio.total_value_usd?.toLocaleString()}`
                }
              </p>
              {displayInBTC && (
                <p className="text-xs text-zinc-500">${portfolio.total_value_usd?.toLocaleString()} USD</p>
              )}
            </div>
            <div className="bg-[#111111] border border-zinc-800 p-4">
              <p className="text-xs text-zinc-500 mb-1">Total P/L</p>
              <p className={`text-2xl font-bold font-data ${portfolio.total_profit_loss >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                {portfolio.total_profit_loss >= 0 ? '+' : ''}${portfolio.total_profit_loss?.toFixed(2)}
              </p>
              <p className={`text-xs ${portfolio.total_profit_loss >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                {portfolio.total_profit_loss_percent >= 0 ? '+' : ''}{portfolio.total_profit_loss_percent?.toFixed(2)}%
              </p>
            </div>
            <div className="bg-[#111111] border border-zinc-800 p-4">
              <p className="text-xs text-zinc-500 mb-1">Holdings</p>
              <p className="text-2xl font-bold font-data text-white">{portfolio.holdings?.length || 0}</p>
            </div>
            <div className="bg-[#111111] border border-zinc-800 p-4">
              <p className="text-xs text-zinc-500 mb-1">BTC Price</p>
              <p className="text-2xl font-bold font-data text-orange-500">${portfolio.btc_price?.toLocaleString()}</p>
            </div>
          </div>
        )}
        
        {/* Quick Add Section + Search */}
        <div className="mb-8">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
            <Plus className="w-5 h-5 text-amber-500" />
            ADD TO PORTFOLIO
          </h2>
          
          {/* Quick Add Popular Assets */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-xs text-zinc-500 self-center mr-2">Quick Add:</span>
            {[
              { symbol: "AAPL", name: "Apple" },
              { symbol: "MSFT", name: "Microsoft" },
              { symbol: "NVDA", name: "NVIDIA" },
              { symbol: "BTC-USD", name: "Bitcoin" },
              { symbol: "ETH-USD", name: "Ethereum" },
              { symbol: "GC=F", name: "Gold" }
            ].map((asset) => (
              <button
                key={asset.symbol}
                onClick={() => {
                  setSearchQuery(asset.symbol);
                  handleSearch(asset.symbol);
                }}
                className="px-3 py-1.5 text-xs bg-zinc-900 border border-zinc-700 text-zinc-400 hover:border-amber-500 hover:text-amber-500 transition-colors"
                data-testid={`quick-add-${asset.symbol}`}
              >
                {asset.name}
              </button>
            ))}
          </div>
          
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Type any symbol: AAPL, TSLA, BTC, GOLD..."
                className="w-full pl-10 pr-10 py-4 bg-zinc-900 border-2 border-zinc-700 focus:border-amber-500 text-lg"
                data-testid="portfolio-search-input"
              />
              {searchQuery && (
                <button 
                  onClick={() => { setSearchQuery(""); setSearchResults([]); }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
              {searchLoading && (
                <RefreshCw className="absolute right-10 top-1/2 transform -translate-y-1/2 w-5 h-5 text-amber-500 animate-spin" />
              )}
            </div>
            
            {/* Search Results - Instant Add */}
            {searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-[#111111] border-2 border-amber-500/50 max-h-80 overflow-y-auto z-20 shadow-xl">
                <div className="p-2 bg-amber-500/10 border-b border-amber-500/30">
                  <p className="text-xs text-amber-500 font-bold">Click to add instantly or tap + to set quantity</p>
                </div>
                {searchResults.map((result) => (
                  <SearchResultCard
                    key={result.symbol}
                    result={result}
                    onAdd={(r) => setAddModalResult(r)}
                  />
                ))}
              </div>
            )}
            
            {searchQuery && !searchLoading && searchResults.length === 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-[#111111] border border-zinc-700 p-4 text-center">
                <p className="text-zinc-400 text-sm">No results for "{searchQuery}"</p>
                <p className="text-zinc-500 text-xs mt-1">Try: AAPL, MSFT, BTC, ETH, GOLD</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Holdings */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <BarChart3 className="w-5 h-5 text-amber-500" />
              MY HOLDINGS
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPortfolio}
              className="border-zinc-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-[#111111] border border-zinc-800 p-4 animate-pulse">
                  <div className="h-6 bg-zinc-800 rounded w-20 mb-4"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-zinc-800 rounded w-full"></div>
                    <div className="h-4 bg-zinc-800 rounded w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : portfolio?.holdings?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {portfolio.holdings.map((holding) => (
                <HoldingCard
                  key={holding.id}
                  holding={holding}
                  onDelete={deleteFromPortfolio}
                  onViewChart={setChartSymbol}
                  onEdit={(h) => console.log("Edit:", h)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-[#111111] border border-zinc-800">
              <PieChart className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No Holdings Yet</h3>
              <p className="text-zinc-500 mb-4">Search for stocks, crypto, or commodities to add to your portfolio</p>
            </div>
          )}
        </div>
      </main>
      
      {/* Chart Modal */}
      <ChartModal
        symbol={chartSymbol}
        isOpen={!!chartSymbol}
        onClose={() => setChartSymbol(null)}
      />
      
      {/* Add to Portfolio Modal */}
      <AddToPortfolioModal
        isOpen={!!addModalResult}
        onClose={() => setAddModalResult(null)}
        searchResult={addModalResult}
        onAdd={addToPortfolio}
      />
    </div>
  );
}
